import { connect } from 'react-redux';
import { useCallback, useContext, useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { lodashGet } from 'functions/lodashGet';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmErknmPositionsSelectOptions, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getCatalogTerminationReasonSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ITerminationReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerminationReasonSelectOption';
import {
  getDecisionFormRef,
  getDecisionPositionsSelectOptions,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';

import styles from './AgreementsTerminated.module.scss';
import { AgreementsSupportingDocumentsAttachment } from '../AgreementsSupportingDocuments/AgreementsSupportingDocumentsAttachment';

interface IAgreementsTerminatedProps {
  agreement: IErknmFormAgreement;
  id: string;
  isWatchOnly: boolean;
  name: string;
}

interface IAgreementsTerminatedStoreProps {
  decisionFormRef: IDecisionFormRef | undefined;
  decisionPositionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  formRef: IKnmFormRef | undefined;
  isSupervisor: boolean;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  terminationReasonsSelectOptions: ITerminationReasonSelectOption[];
}

const AgreementsTerminatedWithoutStore = ({
  agreement,
  id,
  isWatchOnly,
  name,
  decisionFormRef,
  decisionPositionsSelectOptions,
  formRef,
  isSupervisor,
  positionsSelectOptions,
  terminationReasonsSelectOptions,
}: IAgreementsTerminatedProps & IAgreementsTerminatedStoreProps) => {
  const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
  const fieldName = `${name}.terminate`;
  const fieldNameTerminationReasons = `${fieldName}.terminationReasons`;

  const addTerminationReason = useCallback(() => {
    (isDecision ? decisionFormRef : formRef)?.current?.mutators.push(fieldNameTerminationReasons, undefined);
  }, [isDecision, decisionFormRef, formRef]);

  /**
   * Список не использованных оснований для расторжения
   */
  const unusedTerminationReasonsSelectOptions = useMemo(() => {
    return terminationReasonsSelectOptions.filter(
      reasons => !agreement.terminate?.terminationReasons?.find(i => i?.id === reasons.id),
    );
  }, [terminationReasonsSelectOptions]);

  return (
    <FieldBlock
      label={`Сведения о признании соглашения неисполненным и расторжении соглашения`}
      className={styles.Wrapper}
    >
      <FieldBlock
        id={`${id}.terminate.terminationReasons`}
        label={getTitleFieldWithRedStar({
          label: 'Основания для признания соглашения неисполненным и расторжения соглашения',
          field: fieldNameTerminationReasons,
          isKnm,
          isPm,
          isDecision,
        })}
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addTerminationReason}
            disabled={isWatchOnly}
          />
        }
      >
        <div className={styles.ReasonsWrapper}>
          <FieldArray name={fieldNameTerminationReasons} validate={makeRequired(REQUIRED_ERROR)}>
            {({ fields }: FieldArrayRenderProps<ITerminationReasonSelectOption, HTMLElement>) => {
              if (!fields.length) {
                return null;
              }

              return fields.map((terminationReasonsName, index) => {
                const fieldValue: ITerminationReasonSelectOption | undefined = fields.value?.[index];

                return (
                  <Row key={`${name}.terminationReasons.${index}.${fieldValue?.id}`} margin="none" isListItem>
                    <div className={`shared-field-row ${styles.TerminationReasonsRow}`}>
                      <SelectInputField
                        id={`${id}.terminate.terminationReasons[${index}]`}
                        name={terminationReasonsName}
                        placeholder={'Основание'}
                        size="medium"
                        options={unusedTerminationReasonsSelectOptions}
                        disabled={isWatchOnly}
                      />
                      {!isWatchOnly && (
                        <CloseButton
                          className="shared-deleting-row-btn"
                          onClick={() =>
                            (isDecision ? decisionFormRef : formRef)?.current?.mutators.remove(
                              fieldNameTerminationReasons,
                              index,
                            )
                          }
                        />
                      )}
                    </div>
                  </Row>
                );
              });
            }}
          </FieldArray>
        </div>
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            const error = lodashGet(formRef?.current.getState()?.errors || {}, fieldNameTerminationReasons);

            if (error) {
              const errorMessage: string | undefined =
                error && typeof error === 'string'
                  ? (Object.values(error).reduce((prev: string, curr: string) => prev + curr, '') as string)
                  : undefined;

              if (errorMessage && props.touched?.[fieldNameTerminationReasons]) {
                return <p className="shared-input-error">Поле не может быть пустым</p>;
              }
            }

            return null;
          }}
        </FormSpy>
      </FieldBlock>

      <AgreementsSupportingDocumentsAttachment
        id={`${id}.terminate.decisionTerminationAttachment`}
        watchOnly={isWatchOnly}
        isRequired={true}
        name={`${fieldName}.decisionTerminationAttachment`}
        hideButtonAdd={
          !!agreement.terminate?.decisionTerminationAttachment &&
          agreement.terminate?.decisionTerminationAttachment.length > 0
        }
        formRef={isDecision ? decisionFormRef : formRef}
        title={getTitleFieldWithRedStar({
          label: 'Решение о признании соглашения неисполненным и расторжении соглашения',
          field: `${fieldName}.decisionTerminationAttachment`,
          isKnm,
          isPm,
          isDecision,
        })}
        acceptedFiles={'.pdf'}
        type="one"
      />

      <FieldBlock margin="top" label={'Решение принято'}>
        <FieldBlock
          id={`${id}.terminate.prosecutorOrganization`}
          className={styles.FieldBlock}
          label={getTitleFieldWithRedStar({
            label: agreement.terminate?.whoMadeDecision ? 'Наименование органа контроля' : 'Наименования прокуратуры',
            field: `${fieldName}.prosecutorOrganization`,
            isKnm,
            isPm,
            isDecision,
          })}
          placementLabel="left"
          color="black"
        >
          <TextInput disabled value={agreement.terminate?.prosecutorOrganization?.fullName} />
        </FieldBlock>
      </FieldBlock>
      <FieldBlock
        id={`${id}.terminate.fioSigner`}
        label={getTitleFieldWithRedStar({
          label: 'ФИО принявшего решение',
          field: `${fieldName}.fioSigner`,
          isKnm,
          isPm,
          isDecision,
        })}
        color="black"
        className={styles.FieldBlock}
        placementLabel="left"
        margin="bottom"
      >
        <TextInputField name={`${fieldName}.fioSigner`} placeholder={'ФИО'} disabled={isWatchOnly} />
      </FieldBlock>
      <FieldBlock
        id={`${id}.terminate.titleSigner`}
        label={getTitleFieldWithRedStar({
          label: 'Должность лица, принявшего решение',
          field: `${fieldName}.titleSigner${isSupervisor ? '' : '.label'}`,
          isKnm,
          isPm,
          isDecision,
        })}
        color="black"
        className={styles.FieldBlock}
        placementLabel="left"
        margin="bottom"
      >
        {isSupervisor && agreement.status === AgreementStatusEnum.APPROVED ? (
          <SelectInputField
            name={`${fieldName}.titleSigner`}
            placeholder="Выберите должность"
            options={isDecision ? decisionPositionsSelectOptions : positionsSelectOptions}
            isSearchable
            size="medium"
            disabled={isWatchOnly}
          />
        ) : (
          <TextInputField
            name={`${fieldName}.titleSigner.label`}
            placeholder={'Должность'}
            disabled={isWatchOnly}
            onChange={e =>
              // @ts-ignore
              formRef?.current?.change(`${fieldName}.titleSigner`, {
                label: e.target.value,
                code: '',
                guid: '',
                id: '',
                title: e.target.value,
                value: {
                  dictId: '',
                  dictVersionId: '',
                  title: e.target.value,
                },
              })
            }
          />
        )}
      </FieldBlock>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IAgreementsTerminatedStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  terminationReasonsSelectOptions: getCatalogTerminationReasonSelectOptions(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionPositionsSelectOptions(state),
});

export const AgreementsTerminated = connect(mapStateToProps)(AgreementsTerminatedWithoutStore);
